import React from "react";
import { Button, ButtonVariant } from "../atoms/Button";
import { IconComponent } from "../atoms/icons/types";
import styled from "styled-components";
import { PC_HEADER_HEIGHT } from "../../constants/app";
import { COLOR_PRIMARY } from "../../constants/color";
import { Permission, Resource } from "../../constants/permissions";
import { usePermissionErrorHandler } from "../../utils/permissionErrors";
import { useResponsive } from "../../hooks/useResponsive";

type Props = {
  title: string;
  button?: {
    text: string;
    variant: ButtonVariant;
    onClick?: () => void;
    icon?: IconComponent;
    permission?: {
      resource: Resource;
      permission: Permission;
    };
  };
  onPermissionError?: (message: string) => void;
  children: React.ReactNode;
  useStickyTitle?: boolean;
  useFitHeight?: boolean;
};

export const MainTemplate: React.FC<Props> = ({
  title,
  button,
  onPermissionError,
  children,
  useStickyTitle,
  useFitHeight,
}) => {
  const { checkPermissionAndShowError } = usePermissionErrorHandler();
  const { isMobile, isTablet } = useResponsive();

  const handleButtonClick = () => {
    if (button?.permission && onPermissionError) {
      if (!checkPermissionAndShowError(
        button.permission.resource,
        button.permission.permission,
        button.text.toLowerCase(),
        onPermissionError
      )) {
        return;
      }
    }
    button?.onClick?.();
  };
  
  return (
    <StyledMainTemplateContainer>
      <StyledHeaderContainer $useStickyTitle={useStickyTitle} $hasButton={!!button}>
        <StyledTitleContainer>
          <StyledTitle>{title}</StyledTitle>
        </StyledTitleContainer>

        {button && (
          <ButtonContainer>
            <Button {...button} onClick={handleButtonClick} />
          </ButtonContainer>
        )}
      </StyledHeaderContainer>
      
      <StyledTitleDivider />

      <StyledChildrenContainer
        $useFitHeight={useFitHeight}
        $useStickyTitle={useStickyTitle}
        $isMobile={isMobile}
      >
        {children}
      </StyledChildrenContainer>
    </StyledMainTemplateContainer>
  );
};

const StyledMainTemplateContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const StyledHeaderContainer = styled.div<{ $useStickyTitle?: boolean; $hasButton?: boolean }>`
  height: ${({ $hasButton }) => ($hasButton ? PC_HEADER_HEIGHT : 'auto')};
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $hasButton }) => ($hasButton ? '16px 30px 8px 30px' : '12px 30px 6px 30px')};

  @media (max-width: 768px) {
    height: auto;
    min-height: ${({ $hasButton }) => ($hasButton ? '60px' : 'auto')};
    flex-direction: row;
    align-items: center;
    gap: ${({ $hasButton }) => ($hasButton ? '16px' : '0')};
    padding: ${({ $hasButton }) => ($hasButton ? '12px 16px 6px 16px' : '10px 16px 4px 16px')};
  }

  @media (max-width: 480px) {
    padding: ${({ $hasButton }) => ($hasButton ? '10px 12px 5px 12px' : '8px 12px 3px 12px')};
    gap: ${({ $hasButton }) => ($hasButton ? '12px' : '0')};
    min-height: ${({ $hasButton }) => ($hasButton ? '50px' : 'auto')};
  }

  ${({ $useStickyTitle }) =>
    $useStickyTitle
      ? `
    position: sticky;
    top: 0;
    background: white;
    z-index: 1000;
    `
      : ""}
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const StyledTitle = styled.div`
  font-size: 42px;
  font-weight: 700;
  line-height: 100%;
  color: #000000;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 1.3;
  }
`;

const StyledTitleDivider = styled.div`
  width: calc(100% - 60px);
  height: 2px;
  background-color: ${COLOR_PRIMARY};
  margin: 10px 30px 12px 30px;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 8px 16px 10px 16px;
  }

  @media (max-width: 480px) {
    width: calc(100% - 24px);
    margin: 6px 12px 8px 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-shrink: 0;
  }
`;

const StyledChildrenContainer = styled.div<{
  $useFitHeight?: boolean;
  $useStickyTitle?: boolean;
  $isMobile?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 40px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    margin-bottom: 80px;
  }
  
  ${({ $useFitHeight }) =>
    $useFitHeight ? `height: calc(100% - ${PC_HEADER_HEIGHT})` : ""};

  ${({ $useStickyTitle }) =>
    $useStickyTitle
      ? `
      height: calc(100% - ${PC_HEADER_HEIGHT});
      `
      : ""}
      
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;
